/* eslint-disable no-use-before-define */
/* eslint-disable no-irregular-whitespace */
/* eslint-disable no-console */
import React, { FC, useEffect, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { store } from 'redux/store';
import { State } from 'redux/initialState';

import { Partner, User } from 'types';

import presetRecipients from 'constants/presetRecipients';
import {
  MAX_PARTNERS_THRESHOLD,
  TYPE_FILES_ALLOWED,
} from 'constants/defaultConstants';

import { addAlert } from 'modules/_shared/redux/alertActions';
import { getUsers } from 'modules/profile/redux/userActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { sendCommEmail } from 'modules/communications/redux/communicationActions';
import { useGetPartners } from 'modules/partners/hooks/useGetPartners';

import { getSocietyEmails } from 'utils/filters';
import { isValidEmailBody, isValidEmailSubject } from 'utils/cleanText';
import { isDocumentFileAllowed } from 'utils/isDocumentFileAllowed';
import errorSwal from 'utils/errorSwal';

import '../Modals.scss';
import SendEmailHeader from './SendEmailHeader';
import CustomLoading from '../../CustomLoading';

type SendEmailProps = {
  defaultRecipient?: Partner;
};

type RecipientEmail = {
  name: string;
  email: string;
};

const SendEmail: FC<SendEmailProps> = ({ defaultRecipient }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );
  const partners = useSelector((state: State) => state.partners);

  const hasDefaultRecipient = Boolean(defaultRecipient);

  const [isSending, setIsSending] = useState<boolean>(false);

  const [recipients, setRecipients] = useState<
    Record<string, RecipientEmail[]>
  >({
    PARTNERS: [],
    BENEFICIARIES: [],
    INVESTORS: [],
    DIRECTORS: [],
  });

  const [to, setTo] = useState<string | RecipientEmail[]>(
    defaultRecipient
      ? [{ name: defaultRecipient.name, email: defaultRecipient.email }]
      : []
  );
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [presetGroup, setPresetGroup] = useState<string>('');
  const [secondaryEmails, setSecondaryEmails] = useState<RecipientEmail[]>([]);

  useGetPartners({
    societyId: actualSociety?._id,
    items: MAX_PARTNERS_THRESHOLD,
  });

  const storeUniqueRecipients = (
    fromArray: RecipientEmail[],
    toArray: RecipientEmail[],
    uniqueEmails: string[]
  ) => {
    fromArray.forEach((element) => {
      if (!uniqueEmails.includes(element.email)) {
        toArray.push(element);
        uniqueEmails.push(element.email);
      }
    });
  };

  const handleSetRecipients = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedGroup = event.target.value;
    const uniqueRecipients: RecipientEmail[] = [];
    const uniqueEmails: string[] = [];

    setPresetGroup(selectedGroup);

    switch (selectedGroup) {
      // Custom case
      case presetRecipients.CUSTOM: {
        setTo([]);
        break;
      }
      // Partners case
      case presetRecipients.PARTNERS: {
        storeUniqueRecipients(
          recipients.PARTNERS,
          uniqueRecipients,
          uniqueEmails
        );
        storeUniqueRecipients(secondaryEmails, uniqueRecipients, uniqueEmails);
        setTo(uniqueRecipients);
        break;
      }
      // Beneficiaries case
      case presetRecipients.BENEFICIARIES: {
        storeUniqueRecipients(
          recipients.BENEFICIARIES,
          uniqueRecipients,
          uniqueEmails
        );
        setTo(uniqueRecipients);
        break;
      }
      // Investors case
      case presetRecipients.INVESTORS: {
        storeUniqueRecipients(
          recipients.INVESTORS,
          uniqueRecipients,
          uniqueEmails
        );
        setTo(uniqueRecipients);
        break;
      }
      // Directors case
      case presetRecipients.DIRECTORS: {
        storeUniqueRecipients(
          recipients.DIRECTORS,
          uniqueRecipients,
          uniqueEmails
        );
        setTo(uniqueRecipients);
        break;
      }
      // All case
      case presetRecipients.ALL: {
        // Store unique partners
        storeUniqueRecipients(
          recipients.PARTNERS,
          uniqueRecipients,
          uniqueEmails
        );
        // Store unique beneficiaries
        storeUniqueRecipients(
          recipients.BENEFICIARIES,
          uniqueRecipients,
          uniqueEmails
        );
        // Store unique investors
        storeUniqueRecipients(
          recipients.INVESTORS,
          uniqueRecipients,
          uniqueEmails
        );
        // Store unique directors
        storeUniqueRecipients(
          recipients.DIRECTORS,
          uniqueRecipients,
          uniqueEmails
        );
        storeUniqueRecipients(secondaryEmails, uniqueRecipients, uniqueEmails);
        setTo(uniqueRecipients);
        break;
      }
      // Default case
      default: {
        setTo(recipients[selectedGroup]);
        break;
      }
    }
  };

  const getUniqueListBy = (arr: RecipientEmail[], key: string) => [
    ...new Map(
      arr.map((item) => [item[key as keyof typeof item], item])
    ).values(),
  ];

  const validateEmails = (emailsList: RecipientEmail[]) => {
    const allEmails = [
      ...recipients.PARTNERS,
      ...recipients.BENEFICIARIES,
      ...recipients.INVESTORS,
      ...recipients.DIRECTORS,
      ...secondaryEmails,
    ]
      .filter((elem) => elem?.email)
      .map(({ email }) => email.trim());

    let hasInvalidEmails = false;
    const invalidEmails: string[] = [];

    emailsList.forEach(({ email }) => {
      if (!allEmails.includes(email)) {
        hasInvalidEmails = true;
        invalidEmails.push(email);
      }
    });

    return { hasInvalidEmails, invalidEmails };
  };

  const getRecipientsData = (list: string | RecipientEmail[]) => {
    if (presetGroup === presetRecipients.CUSTOM) {
      const recipientsList = (list as string)
        .split(',')
        .map((recipient) => recipient.trim());

      const uniqueList = [...new Set(recipientsList)];

      return uniqueList.map((recipient) => ({
        email: recipient.trim(),
        name: '',
      }));
    }

    const uniqueList = getUniqueListBy(list as RecipientEmail[], 'email');

    return uniqueList;
  };

  const handleChangeSubject = (event: React.ChangeEvent<HTMLInputElement>) => {
    const subjectText = event.target.value;
    setSubject(subjectText);
  };

  const handleChangeBody = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const bodyText = event.target.value;
    setBody(bodyText);
  };

  async function getOtherUsers(users: string[]) {
    if (!users.length) {
      setSecondaryEmails([]);
    } else {
      try {
        const otherUsersList = (await store.dispatch(
          getUsers({
            usersList: users,
          })
        )) as User[];

        const otherUsersEmails =
          otherUsersList?.map((user) => ({
            email: user.email,
            name: user.name,
          })) || [];

        setSecondaryEmails(otherUsersEmails);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    }
  }

  const getSocietySecondaryEmails = () => {
    const userIds: string[] = [];

    partners?.forEach(
      (partner: Partner) =>
        partner?.otherUsers?.length > 0 &&
        partner?.otherUsers?.forEach((user) => userIds.push(user))
    );

    getOtherUsers(userIds);
  };

  const validInputs = () => {
    const validBody = isValidEmailBody(body);
    const validSubject = isValidEmailSubject(subject);

    return validBody && validSubject;
  };

  const handleChangeFiles = async (event: any) => {
    const newFiles = [...event.target.files];

    const validationResults = await Promise.all(
      newFiles.map((file) => isDocumentFileAllowed(file))
    );

    const hasInvalidFiles = validationResults.some((isValid) => !isValid);
    if (hasInvalidFiles) {
      errorSwal(t('InvalidFile'));
      return;
    }

    setFiles([...files, ...newFiles]);
  };

  const handleSendEmail = async () => {
    const hasValidInputs = validInputs();

    if (!hasValidInputs) {
      errorSwal(t('InvalidText'), t('ContactAdministratorMessage'));
      return;
    }

    setIsSending(true);

    try {
      const filteredRecipients = getRecipientsData(to).map((recipient) => {
        const { email, name } = recipient;
        return {
          email: email.trim(),
          name,
        };
      });

      const { hasInvalidEmails } = hasDefaultRecipient
        ? { hasInvalidEmails: false }
        : validateEmails(filteredRecipients);

      if (!hasInvalidEmails) {
        const emailData = {
          to: filteredRecipients,
          subject,
          template: 'sttok-general',
          body: body.split('\n'),
          files,
          presetGroup,
          user: user['_id'],
          society: actualSociety['_id'],
          date: new Date(),
          status: 'SENT',
        };

        dispatch(sendCommEmail(emailData));
      } else {
        dispatch(
          addAlert(alertBodyTypes['ERROR_COMMUNICATIONS_EMAIL_DUPLICATED'])
        );
      }
    } catch (error) {
      dispatch(addAlert(alertBodyTypes['ERROR_COMMUNICATIONS_EMAIL']));
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (actualSociety) {
      setRecipients({
        PARTNERS: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.PARTNERS
        ),
        BENEFICIARIES: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.BENEFICIARIES
        ),
        INVESTORS: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.INVESTORS
        ),
        DIRECTORS: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.DIRECTORS
        ),
      });
    }
    getSocietySecondaryEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualSociety, partners]);

  return (
    <>
      <SendEmailHeader />

      <Modal.Body className="no-padding">
        <div className="nk-block nk-block-lg">
          {!hasDefaultRecipient && (
            <div className="card p-4">
              <div className="card-aside-wrap">
                <div className="card-content">
                  <div className="card-inner p-0">
                    <h6 className="title mb-3">{t('Recipient(s)')}</h6>
                    <ul className="custom-control-group d-flex flex-row justify-content-between">
                      <li>
                        <div className="custom-control custom-control-sm custom-radio custom-control-pro">
                          <input
                            type="radio"
                            className="custom-control-input"
                            name="btnRadio"
                            id="btnRadio1"
                            value={presetRecipients.PARTNERS}
                            disabled={!recipients.PARTNERS.length}
                            onChange={handleSetRecipients}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="btnRadio1"
                          >
                            {t('Partners')}
                          </label>
                        </div>
                      </li>
                      <li>
                        <div className="custom-control custom-control-sm custom-radio custom-control-pro">
                          <input
                            type="radio"
                            className="custom-control-input"
                            name="btnRadio"
                            id="btnRadio2"
                            value={presetRecipients.BENEFICIARIES}
                            disabled={!recipients.BENEFICIARIES.length}
                            onChange={handleSetRecipients}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="btnRadio2"
                          >
                            {t('Beneficiaries')}
                          </label>
                        </div>
                      </li>
                      <li>
                        <div className="custom-control custom-control-sm custom-radio custom-control-pro">
                          <input
                            type="radio"
                            className="custom-control-input"
                            name="btnRadio"
                            id="btnRadio3"
                            value={presetRecipients.INVESTORS}
                            disabled={!recipients.INVESTORS.length}
                            onChange={handleSetRecipients}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="btnRadio3"
                          >
                            {t('Investors')} {t('ConvertibleNote')}
                          </label>
                        </div>
                      </li>
                      <li>
                        <div className="custom-control custom-control-sm custom-radio custom-control-pro">
                          <input
                            type="radio"
                            className="custom-control-input"
                            name="btnRadio"
                            id="btnRadio4"
                            value={presetRecipients.DIRECTORS}
                            disabled={!recipients.DIRECTORS.length}
                            onChange={handleSetRecipients}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="btnRadio4"
                          >
                            {t('AdminOrgan')}
                          </label>
                        </div>
                      </li>
                      <li>
                        <div className="custom-control custom-control-sm custom-radio custom-control-pro checked">
                          <input
                            type="radio"
                            className="custom-control-input"
                            name="btnRadio"
                            id="btnRadio5"
                            value={presetRecipients.ALL}
                            onChange={handleSetRecipients}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="btnRadio5"
                          >
                            {t('All')}
                          </label>
                        </div>
                      </li>
                      <li>
                        <div className="custom-control custom-control-sm custom-radio custom-control-pro checked">
                          <input
                            type="radio"
                            className="custom-control-input"
                            name="btnRadio"
                            id="btnRadio6"
                            value={presetRecipients.CUSTOM}
                            onChange={handleSetRecipients}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="btnRadio6"
                          >
                            {t('Personalized')}
                          </label>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card-content p-0">
            <div className="nk-reply-form-header">
              <div className="nk-reply-form-group">
                <div className="nk-reply-form-input-group">
                  <div className="nk-reply-form-input nk-reply-form-input-to">
                    <label className="label" style={{ width: '25px' }}>
                      {t('EmailTo')}:
                    </label>
                    {/* <tags
                      className="tagify input-mail tagify--noTags tagify--empty"
                      tabIndex="-1"
                    >
                      <span
                        contentEditable=""
                        data-placeholder="Destinatario individual"
                        aria-placeholder="Destinatario individual"
                        className="tagify__input"
                        role="textbox"
                        aria-autocomplete="both"
                        aria-multiline="false"
                      />
                    </tags> */}
                    <input
                      type="text"
                      className="input-mail tagify ml-1"
                      data-whitelist="team@sttok.com, help@sttok.com, contact@sttok.com"
                      value={
                        presetGroup === presetRecipients.CUSTOM
                          ? (to as string)
                          : (to as RecipientEmail[])
                              ?.map((item) => item.email)
                              .join(', ')
                      }
                      onChange={(event) => setTo(event.target.value)}
                      readOnly={
                        presetGroup === '' ||
                        presetGroup !== presetRecipients.CUSTOM
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="nk-reply-form-editor">
              <div className="nk-reply-form-field">
                <input
                  type="text"
                  className="form-control form-control-simple"
                  placeholder={t('Subject')}
                  value={subject}
                  onChange={handleChangeSubject}
                />
              </div>
              <div className="nk-reply-form-field">
                <textarea
                  className="form-control form-control-simple no-resize ex-large"
                  placeholder={t('Text')}
                  value={body}
                  onChange={handleChangeBody}
                />
              </div>
            </div>
            <div className="nk-reply-form-tools">
              <ul className="nk-reply-form-actions g-1">
                <li className="mr-2">
                  <button
                    type="button"
                    className="btn btn-primary mr-2"
                    onClick={handleSendEmail}
                    disabled={!subject || !body || !(to?.length > 0)}
                  >
                    {t('Send')}
                  </button>
                </li>

                <li>
                  <span
                    className={`btn btn-icon btn-sm ${
                      files.length > 0 ? 'text-soft' : 'text-light'
                    }`}
                    data-toggle="tooltip"
                    data-placement="top"
                    title=""
                    data-original-title="Adjuntar documento"
                  >
                    {files.length > 0
                      ? `${files.length} ${t('AttachedFiles')}`
                      : t('NoAttachments')}
                    <em className="icon ni ni-clip-v" />
                  </span>
                </li>
                <li>
                  <div className="form-group">
                    <label className="form-label d-none" htmlFor="comms-file" />
                    <input
                      type="file"
                      id="comms-file"
                      className="form-control-file cursor-pointer w-200px comms-file"
                      accept={TYPE_FILES_ALLOWED}
                      onChange={handleChangeFiles}
                      title="Selecciona un archivo"
                      multiple
                    />
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Modal.Body>

      {isSending && <CustomLoading />}
    </>
  );
};

export default SendEmail;
